import type { Bounds, Matrix2D, ObjectId, SpatialObject, Vec2 } from '@/types';
import { isContainer } from '@/types';
import { boundsOfPoints } from '@/core/geometry/bounds';
import {
  IDENTITY_MATRIX,
  applyMatrix,
  invert,
  multiply,
  objectMatrix,
  transformedCorners,
} from '@/core/geometry/transform';
import { QuadTree } from '@/core/spatial/quadtree';

export type TraversalOrder = 'depth-first' | 'breadth-first';

/**
 * The scene graph is a derived, mutable acceleration structure built from the
 * immutable object map held in the store. It answers spatial and structural
 * questions quickly:
 *
 *  1. What is the world matrix / world bounds of X (composing ancestor transforms)?
 *  2. Which objects are inside this rectangle / precisely under this point?
 *  3. What are the children / descendants / ancestors of X? Would reparenting cycle?
 *
 * It is rebuilt incrementally via `sync()`; React never touches it directly, and
 * document mutations always go through commands — the graph is read-only from the
 * outside.
 */
export class SceneGraph {
  private readonly objects = new Map<ObjectId, SpatialObject>();
  private readonly children = new Map<ObjectId | null, Set<ObjectId>>();
  private readonly worldMatrices = new Map<ObjectId, Matrix2D>();
  private readonly worldBounds = new Map<ObjectId, Bounds>();
  private readonly tree = new QuadTree<ObjectId>({ minX: -4096, minY: -4096, maxX: 4096, maxY: 4096 });

  // ---- lookup -------------------------------------------------------------

  get size(): number {
    return this.objects.size;
  }

  has(id: ObjectId): boolean {
    return this.objects.has(id);
  }

  get(id: ObjectId): SpatialObject | undefined {
    return this.objects.get(id);
  }

  /** Spatial index diagnostics (see QuadTree.stats). */
  indexStats(): ReturnType<QuadTree<ObjectId>['stats']> {
    return this.tree.stats();
  }

  /** Every object id currently in the graph. */
  ids(): ObjectId[] {
    return [...this.objects.keys()];
  }

  /** Objects with no parent. */
  roots(): ObjectId[] {
    return [...(this.children.get(null) ?? [])];
  }

  // ---- hierarchy ----------------------------------------------------------

  childrenOf(parentId: ObjectId | null): readonly ObjectId[] {
    return [...(this.children.get(parentId) ?? [])];
  }

  parentOf(id: ObjectId): ObjectId | null {
    return this.objects.get(id)?.parentId ?? null;
  }

  /** Walks up `parentId` links, nearest ancestor first. Guarded against cycles in corrupt input. */
  ancestorsOf(id: ObjectId): ObjectId[] {
    const out: ObjectId[] = [];
    const seen = new Set<ObjectId>([id]);
    let current = this.objects.get(id)?.parentId ?? null;
    while (current !== null && !seen.has(current)) {
      seen.add(current);
      out.push(current);
      current = this.objects.get(current)?.parentId ?? null;
    }
    return out;
  }

  /** All descendants of `id` in the requested order (default depth-first, pre-order). */
  descendantsOf(id: ObjectId, order: TraversalOrder = 'depth-first'): ObjectId[] {
    return this.traverse(id, order).slice(1);
  }

  /** Nodes reachable from `root` (or the whole scene when `root` is null), root included. */
  traverse(root: ObjectId | null = null, order: TraversalOrder = 'depth-first'): ObjectId[] {
    const out: ObjectId[] = [];
    const seen = new Set<ObjectId>();
    const frontier: ObjectId[] = root === null ? this.roots() : [root];
    while (frontier.length) {
      const id = order === 'depth-first' ? frontier.pop() : frontier.shift();
      if (id === undefined || seen.has(id) || !this.objects.has(id)) continue;
      seen.add(id);
      out.push(id);
      const kids = this.childrenOf(id);
      if (order === 'depth-first') frontier.push(...[...kids].reverse());
      else frontier.push(...kids);
    }
    return out;
  }

  depthOf(id: ObjectId): number {
    return this.ancestorsOf(id).length;
  }

  isAncestorOf(ancestor: ObjectId, id: ObjectId): boolean {
    return this.ancestorsOf(id).includes(ancestor);
  }

  /**
   * True when `id` may become a child of `parentId`: the parent exists, is a
   * container, and is not `id` itself or one of its descendants (which would cycle).
   */
  canReparent(id: ObjectId, parentId: ObjectId | null): boolean {
    if (parentId === null) return this.objects.has(id);
    const parent = this.objects.get(parentId);
    if (!parent || !isContainer(parent) || parentId === id) return false;
    return !this.isAncestorOf(id, parentId);
  }

  // ---- geometry -----------------------------------------------------------

  /** Local → world matrix, composing every ancestor's transform. */
  worldMatrixOf(id: ObjectId): Matrix2D | undefined {
    return this.worldMatrices.get(id);
  }

  /** Axis-aligned world bounds enclosing the (possibly rotated/scaled) object. */
  boundsOf(id: ObjectId): Bounds | undefined {
    return this.worldBounds.get(id);
  }

  /** Union of all world bounds, or null when the graph is empty. */
  contentBounds(): Bounds | null {
    let acc: Bounds | null = null;
    for (const b of this.worldBounds.values()) {
      acc = acc
        ? {
            minX: Math.min(acc.minX, b.minX),
            minY: Math.min(acc.minY, b.minY),
            maxX: Math.max(acc.maxX, b.maxX),
            maxY: Math.max(acc.maxY, b.maxY),
          }
        : b;
    }
    return acc;
  }

  /** Converts a world point into `id`'s local content-box coordinates. */
  worldToLocal(id: ObjectId, world: Vec2): Vec2 | undefined {
    const m = this.worldMatrices.get(id);
    const inv = m ? invert(m) : null;
    return inv ? applyMatrix(inv, world) : undefined;
  }

  /** Exact containment test that respects rotation and scale. */
  containsWorldPoint(id: ObjectId, world: Vec2): boolean {
    const obj = this.objects.get(id);
    const local = this.worldToLocal(id, world);
    if (!obj || !local) return false;
    return local.x >= 0 && local.y >= 0 && local.x <= obj.size.width && local.y <= obj.size.height;
  }

  /** Ids whose world bounds intersect `area` (broad phase). */
  queryBounds(area: Bounds): ObjectId[] {
    return this.tree.query(area);
  }

  /** Whether an object takes part in rendering and hit testing. */
  isInteractive(id: ObjectId): boolean {
    const obj = this.objects.get(id);
    if (!obj || !obj.visible || (obj.kind === 'window' && obj.windowState === 'minimized')) return false;
    return !this.ancestorsOf(id).some((a) => {
      const p = this.objects.get(a);
      return !!p && isContainer(p) && p.collapsed;
    });
  }

  /** The object itself, or the outermost collapsed ancestor that stands in for it. */
  visibleAnchorOf(id: ObjectId): ObjectId {
    let anchor = id;
    for (const a of this.ancestorsOf(id)) {
      const p = this.objects.get(a);
      if (p && isContainer(p) && p.collapsed) anchor = a;
    }
    return anchor;
  }

  /** Interactive objects precisely under `point`, top-most first (highest zIndex). */
  hitTest(point: Vec2): ObjectId[] {
    return this.tree
      .queryPoint(point)
      .filter((id) => this.isInteractive(id) && this.containsWorldPoint(id, point))
      .sort((a, b) => (this.objects.get(b)?.zIndex ?? 0) - (this.objects.get(a)?.zIndex ?? 0));
  }

  // ---- synchronisation ----------------------------------------------------

  /**
   * Reconciles the graph with a new object map. Only changed identities are touched,
   * so this is cheap when the store updates a single object.
   */
  sync(next: Readonly<Record<ObjectId, SpatialObject>>): void {
    for (const id of this.objects.keys()) {
      if (!(id in next)) this.removeObject(id);
    }
    const dirty: ObjectId[] = [];
    for (const key of Object.keys(next) as ObjectId[]) {
      const obj = next[key];
      if (!obj) continue;
      const prev = this.objects.get(key);
      if (prev === obj) continue;
      const reparented = prev?.parentId !== obj.parentId;
      if (prev !== undefined && reparented) this.children.get(prev.parentId)?.delete(key);
      if (reparented) {
        let siblings = this.children.get(obj.parentId);
        if (!siblings) {
          siblings = new Set();
          this.children.set(obj.parentId, siblings);
        }
        siblings.add(key);
      }
      this.objects.set(key, obj);
      dirty.push(key);
    }
    // Recompute matrices/bounds for changed objects and their descendants.
    const visited = new Set<ObjectId>();
    for (const id of dirty) this.recomputeSubtree(id, visited);
  }

  private removeObject(id: ObjectId): void {
    const obj = this.objects.get(id);
    if (!obj) return;
    this.children.get(obj.parentId)?.delete(id);
    this.objects.delete(id);
    this.worldMatrices.delete(id);
    this.worldBounds.delete(id);
    this.tree.remove(id);
  }

  private recomputeSubtree(id: ObjectId, visited: Set<ObjectId>): void {
    if (visited.has(id)) return;
    visited.add(id);
    const obj = this.objects.get(id);
    if (!obj) return;
    const matrix = this.computeWorldMatrix(obj);
    this.worldMatrices.set(id, matrix);
    const bounds = boundsOfPoints(transformedCorners(matrix, obj.size));
    this.worldBounds.set(id, bounds);
    this.tree.update({ id, bounds });
    for (const childId of this.children.get(id) ?? []) this.recomputeSubtree(childId, visited);
  }

  private computeWorldMatrix(obj: SpatialObject): Matrix2D {
    // Compose from the root down: world = M(root) · … · M(parent) · M(obj).
    const chain: SpatialObject[] = [obj];
    const guard = new Set<ObjectId>([obj.id]);
    let parentId = obj.parentId;
    while (parentId !== null && !guard.has(parentId)) {
      guard.add(parentId);
      const parent = this.objects.get(parentId);
      if (!parent) break;
      chain.push(parent);
      parentId = parent.parentId;
    }
    return chain.reduceRight((m, node) => multiply(m, objectMatrix(node.transform, node.size)), IDENTITY_MATRIX);
  }
}
